import type { ButtonHTMLAttributes, ComponentProps, LabelHTMLAttributes } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { BorderProps, ResponsiveValue, SizeProps, SpaceProps } from 'styled-system';
import { alignItems, border, display, height, size, space, width } from 'styled-system';

import type { FillProps, StrokeProps } from '../../theme/system-types';
import type { FlexShrinkProps } from '../../theme/system-utilities';
import { fill, flexShrink, stroke, themeHeight } from '../../theme/system-utilities';
import type { IconName } from '../Icon';
import Icon from '../Icon';
import { Button } from './index';
import type { ButtonBaseProps, ButtonKind, ButtonTrackingProp } from './index';

export type ButtonWithHTMLAttrsProps<T = object> = FillProps &
  SpaceProps &
  BorderProps &
  ButtonBaseProps &
  ButtonTrackingProp &
  ButtonHTMLAttributes<unknown> &
  T;

type ButtonWithLabelHTMLAttrsProps<T> =
  T extends LabelHTMLAttributes<unknown> ? LabelHTMLAttributes<unknown> : object;

type ButtonWithIconWithHTMLAttrsProps<T> = ButtonWithIconProps &
  ButtonWithHTMLAttrsProps<ButtonWithLabelHTMLAttrsProps<T>> & {
    ref?: React.Ref<HTMLButtonElement>;
  };

export interface ButtonWithIconProps {
  iconName?: IconName;
  iconSize?: ResponsiveValue<number>;
  iconGap?: ResponsiveValue<string>;
}

export const DEFAULT_ICON_SIZE = 28;

const UnstyledButtonWithIconWrapper = styled(Button)<any>`
  ${space};
  ${width};
  ${height};
  ${display};
  ${border};
  ${alignItems};
  ${themeHeight};
`;

const ButtonWithIconWrapper = (props: ComponentProps<typeof UnstyledButtonWithIconWrapper>) => (
  <UnstyledButtonWithIconWrapper kind="secondary" display="flex" alignItems="center" {...props} />
);

const UnstyledButtonIcon = styled(Icon)<
  SpaceProps & SizeProps & FlexShrinkProps & FillProps & StrokeProps
>`
  ${space};
  ${size};
  ${flexShrink};
  ${fill};
  ${stroke};
`;

const ButtonIcon = (props: ComponentProps<typeof UnstyledButtonIcon>) => (
  <UnstyledButtonIcon mr={['sp_0', 'sp_4']} flexShrink={0} {...props} />
);

const getIconColor = (kind: ButtonKind) => {
  if (kind === 'primary') {
    return 'sys.neutral.icon.inverted';
  }

  return 'sys.neutral.icon.strong';
};

const ButtonWithIcon = <T extends object>({
  iconSize = DEFAULT_ICON_SIZE,
  iconName,
  iconGap,
  children,
  ref,
  ...props
}: ButtonWithIconWithHTMLAttrsProps<T>) => {
  const iconColor = getIconColor(props.kind);

  return (
    <ButtonWithIconWrapper {...props} ref={ref}>
      {iconName && (
        <ButtonIcon
          name={iconName}
          size={iconSize}
          fill={iconColor}
          stroke={iconColor}
          mr={iconGap}
        />
      )}
      <span>{children}</span>
    </ButtonWithIconWrapper>
  );
};

export default ButtonWithIcon;
